import { Component, inject, OnInit, viewChildren } from '@angular/core';
import { AuthService } from "@core/services/auth.service";
import { TextMessageTemplate } from "@models/text-messages-templates/text-message-template";
import { TextMessagesHelper } from "@models/text-messages-templates/text-messages-helper";
import { TextMessagesTemplateSettingsBase } from "@models/text-messages-templates/text-messages-template-settings-base";
import { ILoadable, toLoadable } from "@models/ui-models/ILoadable";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { IPartialHeaderData } from "@shared/services/header.service";
import { TextMessageApiService } from "@shared/services/sms-templates.service";
import { ToastrService } from "ngx-toastr";
import { combineLatest, finalize, Observable, tap } from "rxjs";
import {
  EditTextMessageTemplateComponent
} from "./components/edit-text-message-template/edit-text-message-template.component";
import {
  SendTextMessageTemplateModalComponent
} from "./components/send-text-messages-template-modal/send-text-message-template-modal.component";

@Component({
  selector: 'app-text-messages-templates-page',
  standalone: true,
  providers: [TextMessageApiService],
  imports: [
    LoaderComponent,
    EditTextMessageTemplateComponent,
  ],
  templateUrl: './text-messages-templates-page.component.html',
  styleUrl: './text-messages-templates-page.component.scss'
})
export class TextMessagesTemplatesPageComponent extends CommonPageComponent implements OnInit {
  private textMessagesTemplateService = inject(TextMessageApiService);
  private modalService = inject(NgbModal);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);

  override readonly headerData: IPartialHeaderData = {
    title: 'SMS Templates',
    iconClass: 'ic-message-20',
    backPath: ['/settings']
  };

  editTextMessageComponents = viewChildren(EditTextMessageTemplateComponent, { read: EditTextMessageTemplateComponent });

  textMessageTemplates: ILoadable<TextMessageTemplate>[] = [];
  defaultTemplatesRecord: Record<string, TextMessagesTemplateSettingsBase> = {};
  testTextMessageTemplateModalOpened = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.isLoading.set(true);
    combineLatest([this.loadTextMessagesTemplates(), this.loadTextMessagesDefaultTemplates()]).subscribe();
  }

  onStartSmsEditing(componentIndex: number): void {
    this.editTextMessageComponents()
      .forEach((editTemplateComponent, index) => {
        if (index !== componentIndex)
          editTemplateComponent.cancelEditing();
      });
  }

  testTextMessageTemplate(smsTemplate: TextMessageTemplate): void {
    if (this.testTextMessageTemplateModalOpened) return;
    this.testTextMessageTemplateModalOpened = false;

    const modalRef = this.modalService.open(SendTextMessageTemplateModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as SendTextMessageTemplateModalComponent;

    instance.phoneByDefault = this.authService.currentUserProfile()?.phoneNumber ?? '';
    instance.textMessageSendRequest = TextMessagesHelper.buildTextMessageSendRequest(smsTemplate, '');

    modalRef.result.then(
      (phone: string) => {
        this.testTextMessageTemplateModalOpened = false;
        this.toastr.success(`Test SMS will be sent to: ${ phone }`);
      },
      () => this.testTextMessageTemplateModalOpened = false
    );
  }

  saveTextMessageTemplate(smsTemplate: TextMessageTemplate, index: number): void {
    this.textMessageTemplates[index].isLoading = true;
    this.textMessagesTemplateService.saveTextMessageTemplate(smsTemplate)
      .pipe(finalize(() => this.textMessageTemplates[index].isLoading = false))
      .subscribe({
        next: (updatedTemplate) => this.textMessageTemplates[index].value = updatedTemplate,
        error: () => this.toastr.error('Something wrong while saving the template', 'Template saving failed')
      });
  }

  showErrorMessage(messageElements: string[]): void {
    this.toastr.error(...messageElements);
  }

  private loadTextMessagesTemplates(): Observable<TextMessageTemplate[]> {
    return this.textMessagesTemplateService.loadTextMessageTemplates()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        tap({
          next: (textMessageTemplates) => this.textMessageTemplates = toLoadable(textMessageTemplates),
          error: () => this.toastr.error('Failed loading of SMS templates', 'Error')
        })
      );
  }

  private loadTextMessagesDefaultTemplates(): Observable<Record<string, TextMessagesTemplateSettingsBase>> {
    return this.textMessagesTemplateService.loadSmsDefaultTemplates()
      .pipe(tap({
        next: (defaultTemplatesRecord) => this.defaultTemplatesRecord = defaultTemplatesRecord,
        error: () => this.toastr.error('Failed loading of default SMS templates', 'Error')
      }));
  }
}
